import { SDKCookies } from '../generated/types'
import { SDKCookieJSON } from '../../config/types/codegen'
import {
  CookieGetResult,
  CookieCtx,
  CookieSetResult,
  ServerCookieAttributes,
} from '../types/cookies'
import SDKCookieRecord from '../generated/SDKCookies.json'

import { CONSENT_COOKIE } from '../consent/config'
import { CONSENT_CATEGORY } from '../../config/types/consent'
import { notInServer } from '../environment'

const cookiePolicies = SDKCookieRecord as SDKCookieJSON

class ServerCookies {
  getString(cookieCtx: CookieCtx, key: SDKCookies): CookieGetResult<string> {
    if (notInServer()) {
      return [false, undefined]
    }

    if (!this.isCookieConsented(cookieCtx, key)) {
      return [false, undefined]
    }

    const rawCookie = cookieCtx.get(key)
    if (!rawCookie) {
      return [true, undefined]
    }

    const decodedCookie = decodeURIComponent(rawCookie)

    return [true, decodedCookie]
  }

  getJSON<T>(cookieCtx: CookieCtx, key: SDKCookies): CookieGetResult<T> {
    const [consented, cookie] = this.getString(cookieCtx, key)
    if (!cookie) {
      return [consented, undefined]
    }

    return [consented, JSON.parse(cookie)]
  }

  setString(
    cookieCtx: CookieCtx,
    key: SDKCookies,
    value: string,
    attributes?: ServerCookieAttributes,
  ): CookieSetResult {
    if (notInServer()) {
      return [false]
    }

    if (!this.isCookieConsented(cookieCtx, key)) {
      return [false]
    }

    const encodedValue = encodeURIComponent(value)
    cookieCtx.set(key, encodedValue, attributes)

    return [true]
  }

  setJSON<T>(
    cookieCtx: CookieCtx,
    key: SDKCookies,
    json: T,
    attributes?: ServerCookieAttributes,
  ): CookieSetResult {
    return this.setString(cookieCtx, key, JSON.stringify(json), attributes)
  }

  private isCookieConsented(cookieCtx: CookieCtx, key: SDKCookies) {
    // Strictly necessary cookies are allowed, even if the user has not consented yet
    if (cookiePolicies[key]?.category === CONSENT_CATEGORY.STRICTLY_NECESSARY) {
      return true
    }

    const consentCookieRaw = cookieCtx.get(CONSENT_COOKIE)
    if (!consentCookieRaw) {
      return false
    }

    const consentCookie = decodeURIComponent(consentCookieRaw)
    const consents = JSON.parse(consentCookie).categories as string[]

    return consents.includes(cookiePolicies[key]?.category)
  }
}

export const serverCookies = new ServerCookies()
